/**
 * Signing in on a corpus site, and the ways out of it to the root site.
 *
 * Its own file because it is the one part of the account controls a stranger
 * meets — and so the part that grew the stranger's cases: an address not yet
 * confirmed, sign-up open or closed, a forgotten password. Every way out leads
 * to the root site, where the account lives, and opens in a new tab so the
 * conversation on this page survives the errand.
 */

import type { JSX } from 'react'

import { useEffect, useState } from 'react'
import type { Account } from './account'
import { AccountError, signIn, siteBootstrap } from './account'
import { Dialog, DialogFailure, Field } from './dialog'
import { t } from './i18n'
import { NEW_CONFIRMATION_URL, PASSWORD_RESET_URL, SIGN_UP_URL } from './origins'

export function SignInDialog({
  onClose,
  onSignedIn,
}: {
  onClose: () => void
  onSignedIn: (account: Account) => void
}): JSX.Element {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [failure, setFailure] = useState<string | null>(null)
  const [isUnconfirmed, setIsUnconfirmed] = useState(false)
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setFailure(null)
    setIsUnconfirmed(false)
    try {
      onSignedIn(await signIn(email, password))
    } catch (error) {
      setFailure(error instanceof AccountError ? error.message : t('error.generic'))
      // The one refusal with a next step: the password was right, the address
      // was never confirmed. Without the link the reader was told what was
      // wrong and given no way to fix it from here.
      // standards: const-duplicated-literal exempt -- the server's own name for this value on the wire; a .tsx cannot reference the
      // Python constant, so the single home stays the server -- see docs/engineering-standards.md.
      setIsUnconfirmed(error instanceof AccountError && error.kind === 'email_unverified')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog title={t('account.signIn')} onClose={onClose}>
      <form onSubmit={submit} className="form">
        <Field
          label={t('account.email')}
          type="email"
          value={email}
          autoComplete="username"
          onChange={setEmail}
        />
        <Field
          label={t('account.password')}
          type="password"
          value={password}
          autoComplete="current-password"
          onChange={setPassword}
        />
        <DialogFailure message={failure} />
        {isUnconfirmed && (
          <p>
            <a href={NEW_CONFIRMATION_URL} target="_blank" rel="noopener">
              {t('account.newConfirmationLink')}
            </a>
          </p>
        )}
        <button type="submit" disabled={busy}>
          {t('account.signIn')}
        </button>
        <WaysOut />
      </form>
    </Dialog>
  )
}

/**
 * The two ways out of the sign-in dialog, both of which live on the root site.
 *
 * A component of its own because it owns state the dialog does not need: it has
 * to ask the server whether sign-up is open here, and a link to a form that
 * would answer 503 is worse than no link.
 *
 * **Null while unknown, and it renders nothing then.** Guessing "closed" would
 * flash a sentence about to become wrong; guessing "open" would offer a link
 * that might not work. The dialog is usable throughout either way — what is
 * missing for a moment is an alternative, not the form.
 *
 * **Both open in a new tab.** The conversation lives only in this page's
 * memory, so following either link in place threw it away — at the very moment
 * a reader decided it was worth keeping, which is the reason sign-in stays on
 * this site at all.
 */
function WaysOut(): JSX.Element | null {
  const [isSignupOpen, setIsSignupOpen] = useState<boolean | null>(null)

  useEffect(() => {
    let cancelled = false
    void siteBootstrap().then((facts) => {
      if (!cancelled) setIsSignupOpen(facts.signupOpen)
    })
    return () => {
      cancelled = true
    }
  }, [])

  if (isSignupOpen === null) return null
  if (!isSignupOpen) return <p className="muted">{t('account.noSignup')}</p>
  return (
    <p className="muted">
      {t('account.signUpPrompt')}{' '}
      <a href={SIGN_UP_URL} target="_blank" rel="noopener">
        {t('account.signUp')}
      </a>
      {' · '}
      <a href={PASSWORD_RESET_URL} target="_blank" rel="noopener">
        {t('account.forgotPassword')}
      </a>
    </p>
  )
}
